import get from 'lodash/get'
import identity from 'lodash/identity'

export default function ($templateCache) {
  'ngInject'
  /**
   * VdSuggestionFormatter provides APIs for enhancing Suggestions with additional data or determining how they are
   * displayed or grouped in the UI.
   *
   * @class VdSuggestionFormatter
   */
  class VdSuggestionFormatter {
    /**
     * Creates an instance of VdSuggestionFormatter.
     *
     * @constructor
     *
     * @param {Object} options
     *        @param {String} options.type
     *               The type of suggestion this formatter is for e.g. 'product', 'brand', 'product_type', etc.
     *        @param {String} [options.displayKey]
     *               The key of the property on the suggestion to display in the suggestions list etc. This can be a
     *               path to a nested property and is relative to the Suggestion object e.g. 'user.display_name'.
     *               Defaults to `${type}.name` (e.g. for a 'product' type it would be 'product.name').
     *        @param {Function} [options.enhance]
     *               Optional function to enhance a suggestion of the specified type with additional information. Will
     *               be passed the Suggestion to enhance and should return the updated suggestion or a promise that
     *               resolves to the updated suggestion. This can be used to sideload related entities (e.g. the outlet
     *               for a register) or supplementary information (e.g. the price of a product).
     *        @param {String} [options.template]
     *               The template to use to render suggestions of this type. The following properties will be
     *               available on the scope:
     *               - $vdSuggestion - the fully enhanced Suggestion to render
     *               - $vdQuery - the current search/filter query
     *               - $vdSource - the VdSuggestionSource that was used to source the suggestion being rendered
     *        @param {String} [options.templateUrl]
     *               Url to the template to use to render suggestions of this type. Template must be in the
     *               $templateCache. This option is ignored if the `template` option is set.
     *        @param {Function} [options.groupBy]
     *               Optional function to determine how suggestions of this type will be grouped in the suggestions
     *               list. Will be passed a fully enhanced Suggestion and must return the displayable String to group
     *               suggestions by. For example the name of the related outlet for a register suggestion. Note this
     *               function must be synchronous: if there are asynchronous things to look up they should be done in
     *               the enhance function and saved on the suggestion. If groupBy is not configured (default) then
     *               suggestions will not be grouped.
     *        @param {Array} [options.groupOrder]
     *               Optional array of suggestion groups in the order they should be displayed. If groupOrder is not specified,
     *               groups are sorted alphabetically.
     */
    constructor({
      type,
      displayKey,
      enhance = identity,
      template,
      templateUrl,
      groupBy,
      groupOrder,
    }) {
      this.type = type
      this._displayKey = displayKey || `${type}.name`
      this._enhance = enhance
      this.groupBy = groupBy
      this.groupOrder = groupOrder

      if (template) {
        this.template = template
      } else if (templateUrl) {
        this.template = $templateCache.get(templateUrl)
      }
    }

    /**
     * Enhance the given suggestion with additional data or related (sideloaded) entities.
     *
     * @method enhance
     * @param  {Suggestion} suggestion The suggestion to enhance
     * @return {Suggestion|Promise<Suggestion>} The updated suggestion or a promise therefor
     */
    enhance(suggestion) {
      return this._enhance(suggestion)
    }

    /**
     * Get the text to display to the user for the given suggestion.
     *
     * @method getDisplayText
     * @param  {Suggestion} suggestion
     * @return {String}
     */
    getDisplayText(suggestion) {
      return get(suggestion, this._displayKey)
    }
  }

  return VdSuggestionFormatter
}
